import { useState, useEffect, useCallback } from 'react';
import UsageInfo from '../../components/UsageInfo';
import { getKonaklamaVergisi } from '../../services/api';
import YilDropdown from '../../components/YilDropdown';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import DataTable from '../../components/DataTable';
import PageHeader from '../../components/PageHeader';

const TUM_AYLAR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

function fmt(n, para = 'TL') {
  if (!isFinite(n) || isNaN(n)) return '—';
  return Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + para;
}

function tarihYaz(t) {
  if (!t) return '—';
  const d = new Date(t);
  return isNaN(d) ? '—' : d.toLocaleDateString('tr-TR');
}

function OzetKart({ icon, label, value, sub, bgClass, textClass, loading }) {
  return (
    <div className={`relative overflow-hidden rounded-[2.5rem] p-5 md:p-6 ${bgClass} border border-white/40 shadow-lg flex flex-col justify-between`}>
      <div className="flex items-start justify-between mb-2">
        <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${textClass}`}>{label}</span>
        <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white/80 shadow-sm font-emoji">{icon}</span>
      </div>
      <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter leading-none">{loading ? '…' : value}</div>
      <p className={`text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 ${textClass}`}>{sub}</p>
    </div>
  );
}

export default function KonaklamaVergisi() {
  const now = new Date();
  // Beyanname izleyen ay verildiği için varsayılan olarak geçen ay açılır.
  const gecenAy = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const [yil,  setYil]  = useState(gecenAy.getFullYear());
  const [ay,   setAy]   = useState(gecenAy.getMonth());     // 0-indexed
  const [oran, setOran] = useState(2);

  const [rapor,   setRapor]   = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata,    setHata]    = useState('');

  const veriGetir = useCallback(async () => {
    setYukleniyor(true); setHata('');
    try {
      const d = await getKonaklamaVergisi(yil, ay + 1, oran);
      setRapor(d);
    } catch (e) {
      setHata(e?.message || 'Rapor alınamadı.');
      setRapor(null);
    } finally {
      setYukleniyor(false);
    }
  }, [yil, ay, oran]);

  useEffect(() => { veriGetir(); }, [veriGetir]);

  const satirlar = rapor?.satirlar || [];

  const BASLIKLAR = ['Rez. No','Oda','Misafir','Firma','Giriş','Çıkış','Ay İçi Gece','Gecelik Bedel','Konaklama Matrahı','Adisyon Matrahı','Vergi'];
  const satirlariCikar = () => satirlar.map(s => [
    s.rezNo || '', s.odaNo || '', s.misafir || '', s.firma || '',
    tarihYaz(s.girisTarihi), s.cikisBelirsiz ? 'Belirsiz' : tarihYaz(s.cikisTarihi),
    s.aydakiGece, s.gecelikBedel, s.matrah, s.adisyonMatrah, s.vergi,
  ]);

  const donemAdi = `${TUM_AYLAR[ay]} ${yil}`;

  const excelIndir = () => downloadExcel(
    `konaklama-vergisi-${yil}-${String(ay + 1).padStart(2, '0')}`,
    'Konaklama Vergisi', BASLIKLAR, satirlariCikar()
  );

  const yazdir = () => printTable(
    'KONAKLAMA VERGİSİ', BASLIKLAR, satirlariCikar(),
    `${donemAdi} — Matrah: ${fmt(rapor?.toplamMatrah)} · Vergi: ${fmt(rapor?.toplamVergi)}`
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        icon="🏛️"
        iconColor="from-violet-600 to-purple-700"
        iconShadow="shadow-violet-200"
        title="Konaklama Vergisi"
        subtitle="Aylık beyanname için tahakkuk raporu"
      >
        <div className="flex flex-wrap gap-2">
          <button onClick={excelIndir} disabled={!satirlar.length}
            className="px-4 py-2 rounded-2xl bg-emerald-500 text-white text-xs font-black uppercase tracking-widest disabled:opacity-40">
            Excel İndir
          </button>
          <button onClick={yazdir} disabled={!satirlar.length}
            className="px-4 py-2 rounded-2xl bg-slate-700 text-white text-xs font-black uppercase tracking-widest disabled:opacity-40">
            Yazdır
          </button>
        </div>
      </PageHeader>

      {/* Filtreler */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-4 md:p-5 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Yıl</label>
          <YilDropdown value={yil} onChange={setYil} />
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Ay</label>
          <select value={ay} onChange={e => setAy(Number(e.target.value))}
            className="px-4 py-2.5 rounded-2xl border border-slate-200 text-sm font-bold text-slate-700 bg-white">
            {TUM_AYLAR.map((a, i) => <option key={a} value={i}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Oran (%)</label>
          <input type="number" min="0" max="100" step="0.5" value={oran}
            onChange={e => setOran(parseFloat(e.target.value) || 0)}
            className="w-24 px-4 py-2.5 rounded-2xl border border-slate-200 text-sm font-bold text-slate-700 text-right" />
        </div>
      </div>

      {hata && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 px-5 py-4 text-sm font-semibold">
          {hata}
        </div>
      )}

      {/* Özet */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <OzetKart icon="🏛️" label="Beyan Edilecek Vergi" loading={yukleniyor}
          value={fmt(rapor?.toplamVergi)} sub={`%${rapor?.oran ?? oran} oran`}
          bgClass="bg-gradient-to-br from-violet-50 to-white" textClass="text-violet-500" />
        <OzetKart icon="💰" label="Toplam Matrah" loading={yukleniyor}
          value={fmt(rapor?.toplamMatrah)}
          sub={rapor?.toplamAdisyonMatrah > 0
            ? `Konaklama ${fmt(rapor?.toplamOdaMatrah)} + adisyon ${fmt(rapor?.toplamAdisyonMatrah)}`
            : 'KDV ve vergi hariç konaklama bedeli'}
          bgClass="bg-gradient-to-br from-emerald-50 to-white" textClass="text-emerald-500" />
        <OzetKart icon="🌙" label="Geceleme" loading={yukleniyor}
          value={rapor?.toplamGeceleme ?? '—'} sub="Ay içindeki toplam gece"
          bgClass="bg-gradient-to-br from-sky-50 to-white" textClass="text-sky-500" />
        <OzetKart icon="📋" label="Rezervasyon" loading={yukleniyor}
          value={rapor?.rezervasyonSayisi ?? '—'} sub="Rapora giren kayıt"
          bgClass="bg-gradient-to-br from-amber-50 to-white" textClass="text-amber-500" />
      </div>

      {rapor?.uyarilar?.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 space-y-1">
          {rapor.uyarilar.map((u, i) => (
            <p key={i} className="text-xs font-semibold text-amber-700">⚠️ {u}</p>
          ))}
        </div>
      )}

      {/* Tablo */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
        {yukleniyor ? (
          <LoadingSpinner text="Rapor hazırlanıyor…" />
        ) : satirlar.length === 0 ? (
          <EmptyState text="Bu ay için konaklama kaydı bulunamadı." icon="🏛️" />
        ) : (
          <DataTable
            anahtarAlan="rezervasyonId"
            satirlar={satirlar}
            bosIkon="🏛️"
            bosMetin="Bu ay için konaklama kaydı bulunamadı."
            toplamGoster
            yogun
            kolonlar={[
              {
                anahtar: 'rezNo', baslik: 'Rez. No', siralanir: true, filtrelenir: true,
                ciz: s => <span className="font-bold text-gray-600">{s.rezNo || '—'}</span>,
                toplam: () => 'TOPLAM',
              },
              { anahtar: 'odaNo', baslik: 'Oda', siralanir: true, filtrelenir: true },
              {
                anahtar: 'misafir', baslik: 'Misafir', siralanir: true, filtrelenir: true,
                ciz: s => <span className="block max-w-xs truncate" title={s.misafir}>{s.misafir || '—'}</span>,
              },
              {
                anahtar: 'firma', baslik: 'Firma', siralanir: true, filtrelenir: true,
                ciz: s => <span className="text-gray-500">{s.firma || '—'}</span>,
              },
              {
                anahtar: 'girisTarihi', baslik: 'Giriş', hizala: 'orta', siralanir: true,
                ciz: s => tarihYaz(s.girisTarihi),
              },
              {
                anahtar: 'cikisTarihi', baslik: 'Çıkış', hizala: 'orta', siralanir: true,
                ciz: s => (s.cikisBelirsiz
                  ? <span className="text-amber-600 font-bold">Belirsiz</span>
                  : tarihYaz(s.cikisTarihi)),
              },
              {
                anahtar: 'aydakiGece', baslik: 'Ay içi gece', hizala: 'orta', siralanir: true,
                ciz: s => <span className="font-black">{s.aydakiGece}</span>,
                toplam: () => rapor?.toplamGeceleme,
              },
              {
                anahtar: 'gecelikBedel', baslik: 'Gecelik', hizala: 'sag', siralanir: true,
                ciz: s => fmt(s.gecelikBedel, s.paraBirimi || 'TL'),
                toplam: () => '',
              },
              {
                anahtar: 'matrah', baslik: 'Konaklama matrahı', hizala: 'sag', siralanir: true,
                ciz: s => fmt(s.matrah, s.paraBirimi || 'TL'),
                toplam: () => fmt(rapor?.toplamOdaMatrah),
              },
              {
                anahtar: 'adisyonMatrah', baslik: 'Adisyon matrahı', hizala: 'sag', siralanir: true,
                ciz: s => <span className="text-gray-500">{s.adisyonMatrah > 0 ? fmt(s.adisyonMatrah, s.paraBirimi || 'TL') : '—'}</span>,
                toplam: () => fmt(rapor?.toplamAdisyonMatrah),
              },
              {
                anahtar: 'vergi', baslik: 'Vergi', hizala: 'sag', siralanir: true,
                ciz: s => <span className="font-black text-violet-600">{fmt(s.vergi, s.paraBirimi || 'TL')}</span>,
                toplam: () => <span className="text-violet-700">{fmt(rapor?.toplamVergi)}</span>,
              },
            ]}
          />
        )}
      </div>

      <UsageInfo
        color="violet"
        description="Konaklama vergisi, konaklama hizmeti veren tesislerin aylık olarak beyan ettiği bir vergidir. Bu rapor, seçilen ayda verilen hizmet üzerinden doğan vergiyi hesaplar."
        scenarios={[
          { title: 'Hangi döneme ait?', content: 'Vergi, hizmetin sunulduğu ayda doğar. Rapor bu yüzden tahsilata değil, o ay içinde geçirilen gecelere göre hesaplanır: gecelik bedel × ay içindeki gece sayısı.' },
          { title: 'Matrah nedir?', content: 'KDV ve konaklama vergisi hariç konaklama bedelidir. Anlaşmalı firma fiyatları vergiler dahil saklandığı için rapor bu tutarlardan matraha geri döner.' },
          { title: 'Adisyonlar neden dahil?', content: 'Geceleme hizmetiyle birlikte satılan hizmetler de verginin konusuna girer; konaklayan misafirin oda hesabına yazılan adisyonları rapora dahildir. Walk-in adisyonlar ve dışarıdan havuz girişleri konaklama olmadığı için kapsam dışıdır.' },
          { title: 'Adisyon KDV oranı', content: 'Her adisyon satırı, satış anında ürünün stok kartındaki KDV oranıyla kaydedilir; oran sonradan değişse bile geçmiş kayıtlar bozulmaz. Oranı tanımlı olmayan ürünlerde otel geneli oran kullanılır ve rapor bunu uyarı olarak bildirir.' },
          { title: 'Çıkışı belirsiz konaklamalar', content: 'Devam eden konaklamalarda hesap bugüne kadar yapılır. Ay tamamlandığında rakam artabilir; beyan öncesi raporu yeniden alın.' },
          { title: 'Beyan zamanı', content: 'Beyanname izleyen ayın 26’sına kadar verilir. Rapor varsayılan olarak geçen ayı açar. Nihai beyan için mali müşavirinize danışın.' },
        ]}
      />
    </div>
  );
}
